import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link2, Mail, Pencil, Phone, UserX } from "lucide-react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { sv } from "date-fns/locale";

interface CustomerHeaderProps {
  customer: {
    id: string;
    full_name: string | null;
    email: string;
    phone: string | null;
    customer_number: number | null;
    created_at: string;
    user_id: string | null;
    level: number | null;
    avatar_url?: string | null;
    blocked?: boolean;
  };
  membershipStatus: string | null;
  onEdit: () => void;
}

function getInitials(name: string | null, email: string): string {
  if (name) {
    const parts = name.trim().split(/\s+/);
    return parts.map((p) => p[0]).join("").slice(0, 2).toUpperCase();
  }
  return email[0]?.toUpperCase() ?? "?";
}

export default function CustomerHeader({ customer, membershipStatus, onEdit }: CustomerHeaderProps) {
  const { t } = useTranslation("admin");
  const initials = getInitials(customer.full_name, customer.email);

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-3">
        <Avatar className="h-14 w-14 shrink-0">
          {customer.avatar_url && <AvatarImage src={customer.avatar_url} alt={customer.full_name ?? ""} />}
          <AvatarFallback className="text-lg font-semibold bg-primary/10 text-primary">
            {initials}
          </AvatarFallback>
        </Avatar>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h2 className="text-lg font-semibold truncate">
              {customer.full_name || customer.email}
            </h2>
            <Button variant="outline" size="sm" className="shrink-0 gap-1" onClick={onEdit}>
              <Pencil className="h-3.5 w-3.5" />
              {t("customer.header.edit")}
            </Button>
          </div>

          <div className="flex flex-col gap-0.5 mt-1">
            <a
              href={`mailto:${customer.email}`}
              className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <Mail className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{customer.email}</span>
            </a>
            {customer.phone && (
              <a
                href={`tel:${customer.phone}`}
                className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <Phone className="h-3.5 w-3.5 shrink-0" />
                {customer.phone}
              </a>
            )}
          </div>

          <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground mt-1">
            {customer.customer_number != null && (
              <span className="font-mono">K-{customer.customer_number}</span>
            )}
            <span>
              {t("customer.header.customerSince")} {format(new Date(customer.created_at), "d MMMM yyyy", { locale: sv })}
            </span>
          </div>
        </div>
      </div>

      {/* Status badges */}
      <div className="flex flex-wrap items-center gap-1.5">
        {customer.user_id ? (
          <Badge className="text-[10px] bg-green-600/15 text-green-700 border-green-300">
            <Link2 className="h-2.5 w-2.5 mr-0.5" />
            {t("customer.header.linked")}
          </Badge>
        ) : (
          <Badge variant="secondary" className="text-[10px]">
            <UserX className="h-2.5 w-2.5 mr-0.5" />
            {t("customer.header.notRegistered")}
          </Badge>
        )}
        {customer.level != null && (
          <Badge variant="outline" className="text-[10px]">
            {t("customer.header.level")} {customer.level}
          </Badge>
        )}
        {membershipStatus === "active" && (
          <Badge className="text-[10px] bg-blue-600/15 text-blue-700 border-blue-300">
            {t("customer.header.member")}
          </Badge>
        )}
        {customer.blocked && (
          <Badge variant="destructive" className="text-[10px]">
            {t("customer.header.blocked")}
          </Badge>
        )}
      </div>
    </div>
  );
}
